import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import { ingestorsApi } from './ingestors/api';
import { ConfigForm } from './ingestors/ConfigForm';
import { SecretForm } from './ingestors/SecretForm';
import type { IngestorType } from './ingestors/types';

interface Team {
  id: string;
  name: string;
  slug: string;
}

interface RepoListEntry {
  full_name: string;
  description: string;
  private: boolean;
  default_branch: string;
  pushed_at: string;
  archived: boolean;
  open_issues_count: number;
}

export function IngestorNewPage() {
  const navigate = useNavigate();
  const types = useQuery({
    queryKey: ['ingestor-types'],
    queryFn: () => ingestorsApi.listTypes(),
  });
  const teams = useQuery({
    queryKey: ['teams'],
    queryFn: () => api<{ teams: Team[] }>('/api/teams'),
  });

  const [type, setType] = useState<string>('');
  const [teamId, setTeamId] = useState<string>('');
  const [name, setName] = useState('');
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [secret, setSecret] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      ingestorsApi.createInstance({
        type,
        team_id: teamId,
        name,
        config,
        secret,
      }),
    onSuccess: (r) => navigate(`/admin/ingestors/${r.instance.id}`),
  });

  const available = types.data?.types ?? [];
  const selectedType: IngestorType | undefined = available.find((t) => t.type === type);

  return (
    <div className="space-y-6">
      <header>
        <Link to="/admin/ingestors" className="text-sm text-ink-muted hover:text-ink">← Ingestors</Link>
        <h1 className="mt-1 text-2xl font-semibold">Add ingestor</h1>
        <p className="text-ink-muted">
          Configure a new data source. The setup instructions are shown on the next page after creation.
        </p>
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          create.mutate(undefined, {
            onError: (e2) => setErr(e2 instanceof ApiError ? e2.message : 'Create failed.'),
          });
        }}
        className="card grid gap-4"
      >
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1">
            <label className="label" htmlFor="type">Type</label>
            <select
              id="type"
              className="input"
              value={type}
              onChange={(e) => { setType(e.target.value); setConfig({}); setSecret({}); }}
              required
            >
              <option value="">Select…</option>
              {available.map((t) => (
                <option key={t.type} value={t.type}>{t.display_name}</option>
              ))}
            </select>
            {selectedType?.description ? (
              <p className="text-xs text-ink-dim">{selectedType.description}</p>
            ) : null}
          </div>
          <div className="space-y-1">
            <label className="label" htmlFor="team">Team</label>
            <select
              id="team"
              className="input"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              required
            >
              <option value="">Select…</option>
              {(teams.data?.teams ?? []).map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            {((teams.data?.teams ?? []).length ?? 0) === 0 ? (
              <p className="text-xs text-accent-danger">
                No teams. <Link to="/admin/teams" className="underline">Create one</Link> first.
              </p>
            ) : null}
          </div>
          <div className="space-y-1">
            <label className="label" htmlFor="name">Name</label>
            <input
              id="name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="GitHub — Forbury/Platform"
              required
              maxLength={80}
            />
          </div>
        </div>

        {selectedType ? (
          <div className="grid gap-6 md:grid-cols-2">
            <section>
              <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink-muted">Config</h2>
              {type === 'github' ? (
                <GithubConfigForm
                  value={config}
                  onChange={setConfig}
                  pat={secret.personalAccessToken ?? ''}
                />
              ) : (
                <ConfigForm type={type} value={config} onChange={setConfig} />
              )}
            </section>
            <section>
              <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink-muted">Secrets</h2>
              <SecretForm type={type} value={secret} onChange={setSecret} required />
              {type === 'github' ? (
                <p className="mt-2 text-xs text-ink-dim">
                  After entering the Personal Access Token above, the <strong>Repository</strong>
                  field in the Config section will populate with the repos the token can see.
                  Pick one, or type an <code>owner/name</code> directly.
                </p>
              ) : null}
            </section>
          </div>
        ) : (
          <p className="text-sm text-ink-muted">Pick a type to show its config + secret fields.</p>
        )}

        {err ? <p className="text-sm text-accent-danger" role="alert">{err}</p> : null}

        <div className="flex justify-end gap-2">
          <Link to="/admin/ingestors" className="btn">Cancel</Link>
          <button
            type="submit"
            className="btn-primary"
            disabled={create.isPending || !type || !teamId || !name}
          >
            {create.isPending ? 'Creating…' : 'Create ingestor'}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * GitHub-specific config form. Wraps the generic ConfigForm with a
 * dynamic repo picker: once the admin has entered a PAT in the
 * Secret form, they can click "Load repos" to fetch the list of
 * repos the PAT can see (org + personal) and pick one from a
 * dropdown. Falls back to a free-text `owner/name` field if the
 * PAT isn't set, the API call fails, or the admin prefers to
 * type the value directly.
 */
function GithubConfigForm({
  value,
  onChange,
  pat,
}: {
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  pat: string;
}) {
  const [repos, setRepos] = useState<RepoListEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [showFreeText, setShowFreeText] = useState(false);

  const load = async () => {
    if (!pat || pat.length < 20) {
      setLoadErr('Enter a Personal Access Token in the Secrets section first.');
      return;
    }
    setLoading(true);
    setLoadErr(null);
    try {
      const r = await api<{ repos: RepoListEntry[] }>(
        '/api/admin/ingestor-types/github/list-repos',
        { method: 'POST', body: { personalAccessToken: pat } },
      );
      setRepos(r.repos);
      if (r.repos.length === 0) setLoadErr('No repos visible to this token.');
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.status}: ${e.message}` : (e as Error).message;
      setLoadErr(msg);
    } finally {
      setLoading(false);
    }
  };

  const currentRepo = (value.repo as string) ?? '';

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <label className="label" htmlFor="cfg-repo">
          Repository (owner/name) <span className="text-accent-danger">*</span>
        </label>
        {repos && !showFreeText ? (
          <div className="space-y-2">
            <select
              id="cfg-repo"
              className="input"
              value={currentRepo}
              onChange={(e) => onChange({ ...value, repo: e.target.value })}
              required
            >
              <option value="">Select a repository…</option>
              {repos.map((r) => (
                <option key={r.full_name} value={r.full_name}>
                  {r.full_name}
                  {r.private ? ' 🔒' : ''}
                  {r.archived ? ' (archived)' : ''}
                  {r.description ? ` — ${r.description.slice(0, 60)}` : ''}
                </option>
              ))}
            </select>
            <div className="flex gap-2 text-xs">
              <button type="button" className="btn" onClick={load} disabled={loading}>
                {loading ? 'Loading…' : `Reload (${repos.length} repos)`}
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => { setRepos(null); setShowFreeText(true); }}
              >
                Type manually
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <input
              id="cfg-repo"
              type="text"
              className="input font-mono"
              value={currentRepo}
              onChange={(e) => onChange({ ...value, repo: e.target.value })}
              placeholder="forbury/platform"
              pattern="^[^/]+/[^/]+$"
              required
            />
            <div className="flex gap-2 text-xs">
              <button type="button" className="btn" onClick={load} disabled={loading || !pat}>
                {loading ? 'Loading repos…' : 'Load repos from GitHub'}
              </button>
              {repos ? (
                <button
                  type="button"
                  className="btn"
                  onClick={() => setShowFreeText(false)}
                >
                  Back to dropdown
                </button>
              ) : null}
            </div>
            {!pat ? (
              <p className="text-xs text-ink-dim">
                Enter a Personal Access Token in the Secrets section, then click
                "Load repos" to pick from the list.
              </p>
            ) : null}
          </div>
        )}
        {loadErr ? <p className="text-xs text-accent-danger" role="alert">{loadErr}</p> : null}
        <p className="text-xs text-ink-dim">
          Polled by the api on the schedule below. The PAT must have
          <code> contents:read</code> + <code>pull_requests:read</code> (or classic <code>repo</code>).
        </p>
      </div>

      <div className="space-y-1">
        <label className="label" htmlFor="cfg-pollInterval">Poll interval (minutes)</label>
        <input
          id="cfg-pollInterval"
          type="number"
          min={5}
          max={1440}
          className="input"
          value={(value.pollIntervalMinutes as number) ?? 10}
          onChange={(e) => onChange({ ...value, pollIntervalMinutes: Number(e.target.value) })}
        />
        <p className="text-xs text-ink-dim">Default 10. Min 5 to respect GitHub rate limits.</p>
      </div>

      <div className="space-y-1">
        <label className="label" htmlFor="cfg-bootstrapDays">Bootstrap window (days)</label>
        <input
          id="cfg-bootstrapDays"
          type="number"
          min={1}
          max={90}
          className="input"
          value={(value.bootstrapDays as number) ?? 7}
          onChange={(e) => onChange({ ...value, bootstrapDays: Number(e.target.value) })}
        />
        <p className="text-xs text-ink-dim">
          How far back to look on the very first poll (before <code>last_poll_at</code> exists).
          Default 7.
        </p>
      </div>

      <div className="flex items-center gap-2 pt-1">
        <input
          id="cfg-autoCreateWebhook"
          type="checkbox"
          className="h-4 w-4 rounded border-white/20 bg-bg-elevated"
          checked={Boolean(value.autoCreateWebhook)}
          onChange={(e) => onChange({ ...value, autoCreateWebhook: e.target.checked })}
        />
        <label htmlFor="cfg-autoCreateWebhook" className="text-sm">
          Auto-register webhook on first event (v1.1)
        </label>
      </div>
    </div>
  );
}
